// SecurityChecks.jsx
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Collapse,
  Alert,
  IconButton,
  LinearProgress,
  CardContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VerifiedIcon from "@mui/icons-material/Verified";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTheme } from "@mui/material/styles";
import { Security } from "@mui/icons-material";

export const SecurityChecks = ({
  checks,
  completedChecks,
  onCheckToggle,
  onStart,
  onReset,
  onUtensilCheck,
}) => {
  const [showSkipWarning, setShowSkipWarning] = useState(false);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());
  const theme = useTheme();

  const completedArray = Array.from(completedChecks);
  const criticalChecks = checks.filter((c) => c.critical);
  const completedCount = checks.filter((c) =>
    completedArray.includes(c.id)
  ).length;
  const progress = (completedCount / checks.length) * 100;

  // Check if all checks are completed
  const allChecksCompleted = completedCount === checks.length;

  // Auto-confirm certain safety checks when component mounts
  useEffect(() => {
    // Create a copy of the current completed checks
    const newCompleted = new Set(completedChecks);

    // Auto-check the lid closing, green signal, and utensil checks
    checks.forEach((check) => {
      if (
        check.id === "lid_closed" ||
        check.id === "green_signal" ||
        check.id === "nutripot_check"
      ) {
        console.log(`Auto-confirming check: ${check.id}`);
        newCompleted.add(check.id);
      }
    });

    // Update the completed checks if we added any
    if (newCompleted.size > completedChecks.size) {
      onCheckToggle(newCompleted);
    }
  }, [checks, completedChecks, onCheckToggle]);

  const handleDismissAlert = (checkId) => {
    setDismissedAlerts((prev) => new Set([...prev, checkId]));
  };

  const handleCheckToggle = (checkId) => {
    const newCompleted = new Set(completedChecks);
    if (newCompleted.has(checkId)) {
      newCompleted.delete(checkId);
    } else {
      newCompleted.add(checkId);
    }
    onCheckToggle(newCompleted);
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(160deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`
            : `linear-gradient(160deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,
        p: 3,
      }}
    >
      <Box
        sx={{
          maxWidth: 800,
          width: "100%",
          p: 4,
          borderRadius: 4,
          textAlign: "center",
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 8px 32px rgba(0, 0, 0, 0.3)`
              : `0 8px 32px rgba(0, 0, 0, 0.1)`,
        }}
      >
        {!allChecksCompleted ? (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
                p: 3,
                borderRadius: 3,
                background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.success.light} 100%)`,
                color: "white",
              }}
            >
              <Security sx={{ fontSize: 40, mr: 2 }} />
              <Typography variant="h4" fontWeight="bold">
                Final Safety Checks
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              {checks.map((check) => (
                <Card
                  key={check.id}
                  sx={{
                    mb: 2,
                    borderLeft: `4px solid ${
                      check.critical
                        ? theme.palette.error.main
                        : theme.palette.success.main
                    }`,
                    backgroundColor: completedChecks.has(check.id)
                      ? theme.palette.success.light + "08"
                      : theme.palette.background.paper,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? `0 4px 12px rgba(0, 0, 0, 0.3)`
                          : `0 4px 12px rgba(0, 0, 0, 0.1)`,
                    },
                  }}
                >
                  <CardContent sx={{ py: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={completedChecks.has(check.id)}
                          onChange={(e) =>
                            handleCheckToggle(check.id, e.target.checked)
                          }
                          color={check.critical ? "error" : "success"}
                          sx={{
                            "& .MuiSvgIcon-root": { fontSize: 28 },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: check.critical
                              ? theme.palette.error.main
                              : "text.primary",
                            ml: 1,
                          }}
                        >
                          {check.message}
                        </Typography>
                      }
                    />
                  </CardContent>
                </Card>
              ))}
            </Box>
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 4,
            }}
          >
            <Typography variant="h3" sx={{ mb: 4, textAlign: "center" }}>
              <VerifiedIcon fontSize="large" color="primary" />
              Safety Verification
            </Typography>

            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 10,
                borderRadius: 5,
                mb: 4,
                background: "rgba(0, 0, 0, 0.1)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 5,
                  background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.success.light} 100%)`,
                },
              }}
            />

            <Box
              sx={{
                mt: 6,
                display: "flex",
                justifyContent: "space-between",
                gap: 3,
              }}
            >
              <Button
                variant="outlined"
                color="error"
                onClick={() => setShowSkipWarning(true)}
                sx={{ flex: 1 }}
              >
                Skip Checks
              </Button>

              <Button
                variant="contained"
                color="primary"
                disabled={criticalChecks.some(
                  (c) => !completedChecks.has(c.id)
                )}
                onClick={onStart}
                sx={{
                  flex: 2,
                  py: 2,
                  fontSize: "1.1rem",
                  boxShadow: 4,
                  "&:disabled": { boxShadow: "none" },
                }}
              >
                Start Cooking
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      <Dialog open={showSkipWarning} onClose={() => setShowSkipWarning(false)}>
        <DialogTitle sx={{ bgcolor: theme.palette.warning.light + "30" }}>
          <WarningAmberIcon color="error" sx={{ mr: 1 }} />
          Safety Override Warning
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ my: 2 }}>
            You are attempting to bypass critical safety checks. This action may
            result in:
          </Typography>
          <ul>
            <li>Equipment damage</li>
            <li>Potential safety hazards</li>
            <li>Recipe failure</li>
          </ul>
        </DialogContent>
        <DialogActions sx={{ bgcolor: theme.palette.warning.light + "30" }}>
          <Button onClick={() => setShowSkipWarning(false)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => {
              setShowSkipWarning(false);
              onStart();
            }}
          >
            Force Start
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
